// ...existing code...
import React, { useEffect, useRef } from "react";
import { useStrokeContext } from "../../services/editor/editorContext";
import { Point, SAMPLE_DISTANCE_PX } from "../../services/editor/editorUtils"



export const DrawingCanvas: React.FC<{
    size:number,
    active: boolean
}> = ({ 
    size,
    active
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const { previewPoints, startStroke, addPoint, endStroke, finished, color, setCanvasSize } = useStrokeContext();
    const lastPtRef = useRef<Point | null>(null);

    const borderColor = active ? "var(--text)" : "var(--gray)";

    // Sync canvas size with context so normalization uses the correct value
    useEffect(() => {
        setCanvasSize(size);
    }, [size, setCanvasSize]);

    useEffect(() => {
        const canvas = canvasRef.current!;
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d")!;
        function draw() {
            ctx.clearRect(0,0,size,size);
            ctx.fillStyle = "#000";
            ctx.fillRect(0,0,size,size);
            const clr = finished ?  color ?? "white" : "white";
            ctx.strokeStyle = clr; ctx.lineWidth = 4; ctx.lineJoin = "round"; ctx.lineCap = "round";
            if (previewPoints.length > 0) {
                ctx.beginPath();
                ctx.moveTo(previewPoints[0].x, previewPoints[0].y);
                for (let i = 1; i < previewPoints.length; i++) ctx.lineTo(previewPoints[i].x, previewPoints[i].y);
                ctx.stroke();
            }
        }
        draw();
    }, [previewPoints, size, color, finished]);

    useEffect(() => {
        const el = canvasRef.current!;
        function getPos(e: PointerEvent): Point {
            const rect = el.getBoundingClientRect();
            // Scale from CSS display pixels to internal canvas buffer pixels
            const scaleX = el.width / rect.width;
            const scaleY = el.height / rect.height;
            const rawX = (e.clientX - rect.left) * scaleX;
            const rawY = (e.clientY - rect.top) * scaleY;
            // Clamp to canvas bounds so points never escape
            return {
                x: Math.max(0, Math.min(el.width, rawX)),
                y: Math.max(0, Math.min(el.height, rawY)),
                t: e.timeStamp
            };
        }
        function onDown(e: PointerEvent) {
            if (finished) return;
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            const p = getPos(e); lastPtRef.current = p; startStroke(p);
        }
        function onMove(e: PointerEvent) {
            if (finished || !lastPtRef.current) return;
            const p = getPos(e);
            const d = Math.hypot(p.x - lastPtRef.current.x, p.y - lastPtRef.current.y);
            if (d >= SAMPLE_DISTANCE_PX) { addPoint(p); lastPtRef.current = p; }
        }
        function onUp(e: PointerEvent) {
            if (!lastPtRef.current) return;
            endStroke();
            lastPtRef.current = null;
        }
        el.addEventListener("pointerdown", onDown);
        el.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            el.removeEventListener("pointerdown", onDown);
            el.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, [startStroke, addPoint, endStroke, finished]);

    return <canvas ref={canvasRef} style={{ 
        width: "100%",
        maxWidth: "500px",
        aspectRatio: "1 / 1", 
        touchAction: "none",
        border: `var(--border-width) solid ${borderColor}`,
    }} />;
};